import type { ICountry, IProvince } from '@/entities/country'
import type { DestinationFormValue } from '@/shared/api/types/destination-types'
import { useTranslation } from 'react-i18next'
import { DestinationFormFields } from '@/features/destination'
import { cn } from '@/shared/libs/cn'
import { showToast } from '@/shared/libs/toast'
import { FormSectionCard } from '@/shared/ui/form-section-card'
import { SectionSaveButton } from '@/shared/ui/section-save-button'

interface DetailsCardProps {
  isEdit: boolean
  isReadonly: boolean
  isLoading: boolean
  form: DestinationFormValue
  onChange: (next: DestinationFormValue) => void
  countries: ICountry[]
  provinces: IProvince[]
  onPreview: (url: string) => void
  onBannerUploadingChange: (uploading: boolean) => void
  onThumbnailUploadingChange: (uploading: boolean) => void
  isSaving: boolean
  isSaveDisabled: boolean
  onSave: () => void
}

/** Country, province, slug, type, banner/thumbnail and active flag. */
export function DetailsCard({
  isEdit,
  isReadonly,
  isLoading,
  form,
  onChange,
  countries,
  provinces,
  onPreview,
  onBannerUploadingChange,
  onThumbnailUploadingChange,
  isSaving,
  isSaveDisabled,
  onSave,
}: DetailsCardProps) {
  const { t } = useTranslation()

  return (
    <FormSectionCard
      title={t('destination.basicInfo', { defaultValue: 'Basic Info' })}
      action={
        isEdit &&
        !isReadonly && (
          <SectionSaveButton disabled={isSaveDisabled} saving={isSaving} onSave={onSave} />
        )
      }
    >
      <div className={cn(isLoading && 'opacity-60 pointer-events-none')}>
        <DestinationFormFields
          value={form}
          countries={countries}
          provinces={provinces}
          onChange={onChange}
          onPreview={onPreview}
          onBannerUploadingChange={onBannerUploadingChange}
          onThumbnailUploadingChange={onThumbnailUploadingChange}
          onUploadSuccess={() =>
            showToast.success(
              t('tour.imageUploadSuccess', { defaultValue: 'Image uploaded successfully' }),
            )
          }
          onUploadError={(message) =>
            showToast.error(
              message || t('tour.imageUploadFailed', { defaultValue: 'Image upload failed' }),
            )
          }
          readonly={isReadonly}
        />
      </div>
    </FormSectionCard>
  )
}
